import { ReactElement, useState, useEffect } from 'react';
import { post, del } from 'utils/network';
import { HeapCustomEvent, ApiRoute } from 'config';
import HeapAnalytics from 'services/HeapAnalytics';

import IApiResponse from 'models/IApiResponse';
import MenuItem from 'models/MenuItem';
import useCurrentUser from 'hooks/useCurrentUser';

import TutorialTooltip from 'components/UpcomingMenus/UpcomingMenu/TutorialTooltip';
import { Component as FavoriteIcon } from 'icons/icon-ui--heart-filled.svg';
import { Component as NotFavoriteIcon } from 'icons/icon-ui--heart-empty.svg';

type Props = {
  item: MenuItem;
  isFavoritesGallery?: boolean;
  itemIndex?: number;
  showFavoritesAnnouncement?: boolean;
};

function FavoriteActions({
  item,
  isFavoritesGallery,
  itemIndex,
  showFavoritesAnnouncement,
}: Props): ReactElement {
  const { sku } = item;
  const pagePathName = window.location.pathname;
  const {
    product_id: productId,
    link_to_product: linkToProduct,
    product_line_name: productLine,
  } = sku;
  const [isFavorite, setFavorite] = useState(sku.favorite);
  const [isFavoriteToggled, setFavoriteToggled] = useState(false);
  const [showFavoriteToolTip, setFavoriteToolTip] = useState(showFavoritesAnnouncement);
  const { currentUser, reload: reloadUser } = useCurrentUser();
  const { id: userId } = currentUser;
  // @ts-expect-error TODO: fix null safety
  useEffect(() => {
    if (!isFavoriteToggled) { return null; }

    const timer = setTimeout(() => {
      if (isFavoritesGallery) {
        const parentContainer = document.getElementById(`recipe-${productId}`);
        // @ts-expect-error TODO: fix null safety
        parentContainer.classList.toggle('fade-out');
        // @ts-expect-error TODO: fix null safety
        parentContainer.ontransitionend = (e) => {
          if (e.target instanceof HTMLElement) {
            e.target.remove();
            if (!document.querySelector('.c-recipe')) {
              reloadUser();
            }
          }
        };
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isFavoriteToggled, isFavoritesGallery, productId, reloadUser]);

  const favoriteActionHeap = (action: string) => {
    HeapAnalytics.track(
      action,
      {
        user_id: userId,
        page: pagePathName,
        recipe_id: productId,
        product_line: productLine,
        recipe_url: linkToProduct,
      },
    );
  };

  const handleCreate = () => {
    favoriteActionHeap(HeapCustomEvent.FAVORITE_PRODUCT_ADDED);
    (async () => {
      const payload = { product_id: productId };
      const response = await post<IApiResponse>(ApiRoute.FAVORITES, payload);
      if (response.status === 201) {
        setFavorite(true);
        setFavoriteToggled(true);
      }
    })();
  };

  const handleDelete = () => {
    favoriteActionHeap(HeapCustomEvent.FAVORITE_PRODUCT_REMOVED);
    (async () => {
      const response = await del<IApiResponse>(`${ApiRoute.FAVORITES}/${productId}`);
      if (response.status === 204) {
        setFavorite(false);
        setFavoriteToggled(true);
      }
    })();
  };

  const handleAnimationClass = () => {
    let className = '';
    if (isFavoriteToggled) {
      className = isFavorite ? 'is-favorited' : 'is-unfavorited';
    }
    return className;
  };

  return (
    <>
      <button
        type="button"
        role="switch"
        key={productId}
        className={`c-button c-favorite c-favorite--${productId} ${handleAnimationClass()}`}
        aria-checked={isFavorite}
        aria-label={isFavorite ? 'Unfavorite' : 'Favorite'}
        onClick={() => (isFavorite ? handleDelete() : handleCreate())}
      >
        {isFavorite ? (
          <span>
            <FavoriteIcon width="26" />
            Added to Favorites
          </span>
        ) : (
          <span>
            <NotFavoriteIcon width="26" />
            Removed from Favorites
          </span>
        )}
      </button>
      {showFavoriteToolTip && (
        (itemIndex === 0) && (
          <TutorialTooltip isFavoriteAnnouncement onClose={() => setFavoriteToolTip(false)} />
        )
      )}
    </>
  );
}

export default FavoriteActions;
